import { Injectable, OnDestroy } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { ErrorDCR } from '@ddt-web/config';
import { AppStoreConfig, PartnerApiService } from '@ddt-web/data-service';
import { Config, StoreService } from '@ddt-web/store-service';
import { PartnerConstants, PartnerUserProfileService } from '@ddt-web/user-data';
import * as get from 'lodash/get';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { PartnerErrorHelper } from './partner-error-helper';
import { PartnerStoreHelper } from './partner-store-helper';

@Injectable({ providedIn: 'root' })
export class PartnerDataService implements OnDestroy {
	private programHandleListOptions$ = new BehaviorSubject([]);
	private riskScaleListOptions$ = new BehaviorSubject([]);
	private dataSubscription: Subscription;

	constructor(
		private partnerApiService: PartnerApiService,
		private storeService: StoreService,
		private partnerUserProfileService: PartnerUserProfileService,
		private partnerStoreHelper: PartnerStoreHelper,
		private partnerErrorHelper: PartnerErrorHelper,
		private contentService: ContentService
	) { }

	public getPartnerProgramAndRiskConfig(storeConfig: AppStoreConfig, skipErrorHandler = false): void {
		this.dataSubscription = combineLatest(this.storeService.selectAll(storeConfig.partnerConfig), this.partnerUserProfileService.getPartner())
			.subscribe(([config, partner]) => {
				if (!config.isLoading) {
					this.programHandleListOptions$.next(
						Object.values(get(config, 'entities.programHandles', {}))
							.filter(program => PartnerConstants.ENVESTNET_SUPPORTED_PROGRAM_TYPE_IDS.includes(program['programTypeId']))
							.map(program => {
								return { name: program['programName'], handle: program['programHandle'], programTypeId: program['programTypeId'] };
							})
					);
					this.riskScaleListOptions$.next(
						Object.values(get(config, 'entities.riskScales', {}))
							.map(riskScale => {
								return { name: riskScale['name'], handle: riskScale['handle'] };
							})
					);
				} else if (PartnerConstants.ACTIVATED_PARTNERS.includes(partner)) {
					this.fetchProgramHandles(storeConfig.partnerConfig, partner, skipErrorHandler);
					this.fetchRiskScales(storeConfig.partnerConfig, partner, skipErrorHandler);
				}
			});
	}

	/**
	 * Fetching program handles from a partner.
	 * If error, the config store is updated with empty array and an error is thrown.
	 *
	 * @param {Config} config
	 * @param {string} partnerSource
	 * @param {boolean} skipErrorHandler enable/disable popup error
	 * @memberof PartnerPortfolioDataService
	 */
	public fetchProgramHandles<T>(config: Config<T>, partnerSource: string, skipErrorHandler = false): void {
		this.partnerApiService.fetchExternalProgramHandles(partnerSource).subscribe(
			responseJson => {
				const response = this.getExternalHandleResponse(responseJson);
				this.partnerStoreHelper.updateStoreFromHandleResponse([config], response, 'programHandles', responseJson, skipErrorHandler);
			},
			error => {
				this.handleNoProgramsTargetRiskError(error, config, skipErrorHandler);
			}
		);
	}

	/**
	 * Fetching risk scales from a partner.
	 * If error, the config store is updated with empty array and an error is thrown.
	 *
	 * @param {Config} config
	 * @param {string} partnerSource
	 * @param {boolean} skipErrorHandler enable/disable popup error
	 * @memberof PartnerPortfolioDataService
	 */
	public fetchRiskScales<T>(config: Config<T>, partnerSource: string, skipErrorHandler = false): void {
		this.partnerApiService.fetchExternalRiskScales(partnerSource).subscribe(
			responseJson => {
				const response = this.getExternalHandleResponse(responseJson);
				this.partnerStoreHelper.updateStoreFromHandleResponse([config], response, 'riskScales', responseJson, skipErrorHandler);
			},
			error => {
				this.handleNoProgramsTargetRiskError(error, config, skipErrorHandler);
			}
		);
	}
	/**
	 * Handles error from the program handle or risk scale call
	 */
	private handleNoProgramsTargetRiskError<T>(error: any, config: Config<T>, skipErrorHandler): void {
		this.storeService.upsertMany(config, []);
		if (!skipErrorHandler) {
			this.partnerErrorHelper.handleResponseError(error, this.contentService.get('PARTNER_NO_PROGRAMS_TARGET_RISK_ERROR', ErrorDCR.PARTNER_NO_PROGRAMS_TARGET_RISK_ERROR));
		}
	}

	/**
	 * Extracts the response from get-program-handles and get-risk-scales call
	 *
	 * @param {*} responseJson
	 * @returns {any[]}
	 * @memberof PartnerPortfolioDataService
	 */
	public getExternalHandleResponse(responseJson: any): any[] {
		if (responseJson && responseJson.success && responseJson.response && responseJson.response.length > 0) {
			return get(responseJson, 'response');
		}
	}

	public getProgramHandleListOptions$(): Observable<any[]> {
		return this.programHandleListOptions$.asObservable().pipe(distinctUntilChanged());
	}

	public getRiskScaleListOptions$(): Observable<any[]> {
		return this.riskScaleListOptions$.asObservable().pipe(distinctUntilChanged());
	}

	public ngOnDestroy(): void {
		this.dataSubscription.unsubscribe();
	}
}
